import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Info } from "lucide-react";
import FormField from "components/admin/FormField";
import ActionButton from "components/admin/ActionButton";
import { datePickerPopperProps } from "components/admin/datePickerPopper";
import WinnerUserPicker, { WinnerChipItem } from "./WinnerUserPicker";
import WinnerPreviewCard, { PreviewCategory } from "./WinnerPreviewCard";
import ConfirmDeleteModal from "../creditsManagement/DeleteModel";
import { GiveawayContest, GiveawayUserOption } from "types/giveaway";
import {
  GiveawayWinnerDraft,
  deleteGiveawayWinner,
  getGiveawayContestById,
  getGiveawayWinners,
  publishGiveawayWinners,
  retryGiveawayWinnerCredit,
} from "apis/adminServices/giveawayServices";
import { getApiErrorMessage } from "apis/adminServices/userManagmentService";
import { useToastStore } from "store/toastStore";
import {
  ordinal,
  toApiDateTime,
  toDate,
  winnerDisplayName,
} from "./giveawayFormUtils";

const LIST_PATH = "/admin/giveaway-management";
// The pickers offer 15-minute slots, so the display window (like the contest
// window) must be at least one slot wide — start and end can't land on the
// same moment.
const MIN_DISPLAY_GAP_MINUTES = 15;
const MIN_DISPLAY_GAP_MS = MIN_DISPLAY_GAP_MINUTES * 60 * 1000;

interface WinnerErrors {
  winner1st?: string;
  winner2nd?: string;
  winner3rd?: string;
  additionalWinners?: string;
  displayPeriod?: string;
}

const idsOf = (items: WinnerChipItem[]) =>
  items
    .map((item) => item.userId)
    .sort((a, b) => a - b)
    .join(",");

/**
 * Publish / manage winners for a giveaway contest.
 *
 * 1st/2nd/3rd Prize are single-winner slots published in sequence (2nd needs
 * 1st, 3rd needs 2nd); Additional Winners is a capped multi-select. Every
 * winner published in one action shares a single Display Period.
 */
const GiveawayWinnerForm: React.FC = () => {
  const { contestId } = useParams<{ contestId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToastStore();

  const [contest, setContest] = useState<GiveawayContest | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [retryingWinnerIds, setRetryingWinnerIds] = useState<number[]>([]);

  const [winner1st, setWinner1st] = useState<WinnerChipItem[]>([]);
  const [winner2nd, setWinner2nd] = useState<WinnerChipItem[]>([]);
  const [winner3rd, setWinner3rd] = useState<WinnerChipItem[]>([]);
  const [additionalWinners, setAdditionalWinners] = useState<WinnerChipItem[]>([]);

  const [displayFrom, setDisplayFrom] = useState<Date | null>(null);
  const [displayTo, setDisplayTo] = useState<Date | null>(null);

  // Whether any winner had already been published when the screen loaded —
  // once true, Winner Name pickers lock for the rest of this visit even if
  // the admin later removes every selection in memory.
  const [anyPublished, setAnyPublished] = useState(false);

  const initialWinner1stRef = useRef<WinnerChipItem[]>([]);
  const initialWinner2ndRef = useRef<WinnerChipItem[]>([]);
  const initialWinner3rdRef = useRef<WinnerChipItem[]>([]);
  const initialAdditionalWinnersRef = useRef<WinnerChipItem[]>([]);
  const initialDisplayFromRef = useRef<Date | null>(null);
  const initialDisplayToRef = useRef<Date | null>(null);

  const [errors, setErrors] = useState<WinnerErrors>({});

  const [confirmingMissingRewards, setConfirmingMissingRewards] = useState(false);
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const displayFromPickerRef = useRef<DatePicker>(null);
  const displayToPickerRef = useRef<DatePicker>(null);

  const loadWinnersData = async (cId: number) => {
    try {
      const [loadedContest, winners] = await Promise.all([
        getGiveawayContestById(cId),
        getGiveawayWinners(cId),
      ]);

      if (!loadedContest) {
        showToast("Contest not found", "error", 3000);
        navigate(LIST_PATH, { replace: true });
        return;
      }
      setContest(loadedContest);

      const toChip = (w: (typeof winners)[number]): WinnerChipItem => ({
        winnerId: w.winnerId,
        userId: w.userId!,
        userName: winnerDisplayName(w),
        email: w.email,
        profilePictureUrl: w.profilePictureUrl,
        status: w.status,
        creditError: w.creditError,
      });

      const loaded1st = winners.filter(
        (w) => !w.isAdditionalWinner && w.position === 1 && typeof w.userId === "number",
      ).map(toChip);
      const loaded2nd = winners.filter(
        (w) => !w.isAdditionalWinner && w.position === 2 && typeof w.userId === "number",
      ).map(toChip);
      const loaded3rd = winners.filter(
        (w) => !w.isAdditionalWinner && w.position === 3 && typeof w.userId === "number",
      ).map(toChip);
      const loadedAdditional = winners.filter(
        (w) => w.isAdditionalWinner && typeof w.userId === "number",
      ).map(toChip);

      setWinner1st(loaded1st);
      setWinner2nd(loaded2nd);
      setWinner3rd(loaded3rd);
      setAdditionalWinners(loadedAdditional);
      initialWinner1stRef.current = loaded1st;
      initialWinner2ndRef.current = loaded2nd;
      initialWinner3rdRef.current = loaded3rd;
      initialAdditionalWinnersRef.current = loadedAdditional;

      setAnyPublished(winners.some((w) => !!w.winnerId));

      // Display Period is shared across every winner — read it off any row.
      const sample = winners[0];
      const from = toDate(sample?.displayFrom);
      const to = toDate(sample?.displayTo);
      setDisplayFrom(from);
      setDisplayTo(to);
      initialDisplayFromRef.current = from;
      initialDisplayToRef.current = to;

      setErrors({});
    } catch (error) {
      console.error("Failed to load winner screen:", error);
      showToast("Failed to load winner details", "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!contestId) return;
    setLoading(true);
    loadWinnersData(Number(contestId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId]);

  const reward2ndConfigured = !!contest?.reward2nd?.trim();
  const reward3rdConfigured = !!contest?.reward3rd?.trim();
  const additionalWinnersCap = contest?.additionalWinnersCount ?? 0;
  const additionalWinnersConfigured = additionalWinnersCap > 0;

  // Winner Name pickers lock once any winner has been published for this
  // contest; the Display Period stays editable as long as it's still ahead.
  const displayPeriodLocked =
    anyPublished && !(displayTo && displayTo.getTime() > Date.now());
  // Once published, Start Date & Time stays editable only while it is still
  // in the future; once the clock reaches the start date, it locks.
  const displayStartLocked =
    anyPublished && !(displayFrom && displayFrom.getTime() > Date.now());
  const winnersLocked = anyPublished;

  const isDirty = useMemo(() => {
    if (idsOf(winner1st) !== idsOf(initialWinner1stRef.current)) return true;
    if (idsOf(winner2nd) !== idsOf(initialWinner2ndRef.current)) return true;
    if (idsOf(winner3rd) !== idsOf(initialWinner3rdRef.current)) return true;
    if (idsOf(additionalWinners) !== idsOf(initialAdditionalWinnersRef.current)) return true;
    if (displayFrom?.getTime() !== initialDisplayFromRef.current?.getTime()) return true;
    if (displayTo?.getTime() !== initialDisplayToRef.current?.getTime()) return true;
    return false;
  }, [winner1st, winner2nd, winner3rd, additionalWinners, displayFrom, displayTo]);

  const handleNavigateAway = () => {
    if (isDirty) {
      setConfirmingDiscard(true);
    } else {
      navigate(LIST_PATH);
    }
  };

  const clearSlotError = (key: keyof WinnerErrors) =>
    setErrors((prev) => ({ ...prev, [key]: undefined }));

  const handleAddAdditionalWinner = (user: GiveawayUserOption) => {
    if (additionalWinners.some((w) => w.userId === user.userId)) return;
    if (additionalWinners.length >= additionalWinnersCap) {
      setErrors((prev) => ({
        ...prev,
        additionalWinners: "Maximum number of winners reached.",
      }));
      return;
    }
    setAdditionalWinners((prev) => [
      ...prev,
      {
        userId: user.userId,
        userName: user.userName,
        email: user.email,
        profilePictureUrl: user.profilePictureUrl,
      },
    ]);
    // Surface the cap message as soon as this add fills the last slot,
    // rather than waiting for a further (blocked) attempt to add one more.
    if (additionalWinners.length + 1 >= additionalWinnersCap) {
      setErrors((prev) => ({
        ...prev,
        additionalWinners: "Maximum number of winners reached.",
      }));
    } else {
      clearSlotError("additionalWinners");
    }
  };

  const handleRemoveAdditionalWinner = (userId: number) => {
    setAdditionalWinners((prev) => prev.filter((w) => w.userId !== userId));
    // A slot just opened, so the cap message no longer applies.
    clearSlotError("additionalWinners");
  };

  const handleRetryCredit = async (winnerId: number) => {
    if (retryingWinnerIds.includes(winnerId)) return;
    setRetryingWinnerIds((prev) => [...prev, winnerId]);
    try {
      await retryGiveawayWinnerCredit(winnerId);
      showToast("Tokens credited successfully!", "success", 3000);
      if (contestId) {
        await loadWinnersData(Number(contestId));
      }
    } catch (error) {
      console.error("Failed to retry credit:", error);
      showToast(
        getApiErrorMessage(error, "Token credit retry failed. Please try again later."),
        "error",
        4000,
      );
    } finally {
      setRetryingWinnerIds((prev) => prev.filter((id) => id !== winnerId));
    }
  };

  const contestEndDate = toDate(contest?.endDate);

  const missingRewardCategories = useMemo(() => {
    const missing: string[] = [];
    if (reward2ndConfigured && winner2nd.length === 0) missing.push("2nd Prize");
    if (reward3rdConfigured && winner3rd.length === 0) missing.push("3rd Prize");
    if (additionalWinnersConfigured && additionalWinners.length === 0) {
      missing.push("Additional Winners");
    }
    return missing;
  }, [reward2ndConfigured, reward3rdConfigured, additionalWinnersConfigured, winner2nd, winner3rd, additionalWinners]);

  const joinWithAnd = (items: string[]) => {
    if (items.length <= 1) return items.join("");
    if (items.length === 2) return items.join(" and ");
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
  };

  const validate = (): boolean => {
    const next: WinnerErrors = {};

    if (contestEndDate && contestEndDate.getTime() > Date.now()) {
      showToast(
        `Winners can only be added after the contest ends. Contest end date: ${format(
          contestEndDate,
          "dd/MM/yyyy HH:mm",
        )}.`,
        "error",
        5000,
      );
      return false;
    }

    const hasAny =
      winner1st.length > 0 ||
      winner2nd.length > 0 ||
      winner3rd.length > 0 ||
      additionalWinners.length > 0;

    if (!hasAny) {
      next.winner1st = "At least one winner is required";
    }

    if (winner2nd.length > 0 && winner1st.length === 0) {
      next.winner2nd = "Add the 1st Prize winner before adding a 2nd Prize winner.";
    }
    if (winner3rd.length > 0 && winner2nd.length === 0) {
      next.winner3rd = "Add the 2nd Prize winner before adding a 3rd Prize winner.";
    }

    if (winner2nd.length > 0 && !reward2ndConfigured) {
      next.winner2nd = "2nd Prize reward has not been added.";
    }
    if (winner3rd.length > 0 && !reward3rdConfigured) {
      next.winner3rd = "3rd Prize reward has not been added.";
    }

    if (additionalWinners.length > additionalWinnersCap) {
      next.additionalWinners = "Maximum number of winners reached.";
    }

    const allIds = [
      ...winner1st.map((w) => w.userId),
      ...winner2nd.map((w) => w.userId),
      ...winner3rd.map((w) => w.userId),
      ...additionalWinners.map((w) => w.userId),
    ];
    if (new Set(allIds).size !== allIds.length) {
      next.additionalWinners = "This user has already been added as a winner for this prize.";
    }

    if (hasAny && !displayPeriodLocked) {
      const now = new Date();
      if (!displayFrom || !displayTo) {
        next.displayPeriod = "Both start and end date & time are required";
      } else {
        if (!displayStartLocked && contestEndDate && displayFrom.getTime() < contestEndDate.getTime()) {
          next.displayPeriod = "Display start date cannot be before the contest end date.";
        } else if (!displayStartLocked && displayFrom.getTime() < now.getTime()) {
          next.displayPeriod = "Start date & time cannot be in the past";
        } else if (displayTo.getTime() < now.getTime()) {
          next.displayPeriod = "End date & time cannot be in the past";
        }
        if (displayTo.getTime() - displayFrom.getTime() < MIN_DISPLAY_GAP_MS) {
          next.displayPeriod = `Display end date & time must be at least ${MIN_DISPLAY_GAP_MINUTES} minutes after the display start.`;
        }
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handlePublishClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !contestId) return;

    if (missingRewardCategories.length > 0 && !winnersLocked) {
      setConfirmingMissingRewards(true);
      return;
    }

    setConfirmingPublish(true);
  };

  const executePublish = async () => {
    if (!contestId || submitting) return;
    setSubmitting(true);

    try {
      const cId = Number(contestId);
      const from = toApiDateTime(displayFrom);
      const to = toApiDateTime(displayTo);

      const drafts: GiveawayWinnerDraft[] = [];
      if (winner1st[0]) {
        drafts.push({
          ...(winner1st[0].winnerId ? { winnerId: winner1st[0].winnerId } : {}),
          position: 1,
          isAdditionalWinner: false,
          userId: winner1st[0].userId,
          displayFrom: from,
          displayTo: to,
        });
      }
      if (winner2nd[0]) {
        drafts.push({
          ...(winner2nd[0].winnerId ? { winnerId: winner2nd[0].winnerId } : {}),
          position: 2,
          isAdditionalWinner: false,
          userId: winner2nd[0].userId,
          displayFrom: from,
          displayTo: to,
        });
      }
      if (winner3rd[0]) {
        drafts.push({
          ...(winner3rd[0].winnerId ? { winnerId: winner3rd[0].winnerId } : {}),
          position: 3,
          isAdditionalWinner: false,
          userId: winner3rd[0].userId,
          displayFrom: from,
          displayTo: to,
        });
      }
      additionalWinners.forEach((w) => {
        drafts.push({
          ...(w.winnerId ? { winnerId: w.winnerId } : {}),
          position: null,
          isAdditionalWinner: true,
          userId: w.userId,
          displayFrom: from,
          displayTo: to,
        });
      });

      const winnersToDelete: number[] = [];
      initialWinner1stRef.current.forEach((w) => {
        if (w.winnerId && !winner1st.some((c) => c.userId === w.userId)) winnersToDelete.push(w.winnerId);
      });
      initialWinner2ndRef.current.forEach((w) => {
        if (w.winnerId && !winner2nd.some((c) => c.userId === w.userId)) winnersToDelete.push(w.winnerId);
      });
      initialWinner3rdRef.current.forEach((w) => {
        if (w.winnerId && !winner3rd.some((c) => c.userId === w.userId)) winnersToDelete.push(w.winnerId);
      });
      initialAdditionalWinnersRef.current.forEach((w) => {
        if (w.winnerId && !additionalWinners.some((c) => c.userId === w.userId)) winnersToDelete.push(w.winnerId);
      });

      if (winnersToDelete.length > 0) {
        await Promise.all(winnersToDelete.map((wId) => deleteGiveawayWinner(wId)));
      }

      if (drafts.length > 0) {
        const { tokensCredited, failures } = await publishGiveawayWinners(cId, drafts);

        if (failures.length > 0) {
          showToast(
            failures
              .map((f) => `${f.position ? `${ordinal(f.position)} Prize` : "Additional Winner"}: ${f.message}`)
              .join(" · "),
            "error",
            6000,
          );
          await loadWinnersData(cId);
          return;
        }

        showToast(
          tokensCredited > 0
            ? `Winners published — ${tokensCredited} tokens credited`
            : winnersLocked
            ? "Announcement updated successfully"
            : "Winners published successfully",
          "success",
          3000,
        );
      } else if (winnersToDelete.length > 0) {
        showToast("Winners updated successfully", "success", 3000);
      }

      navigate(LIST_PATH);
    } catch (error) {
      console.error("Failed to publish winners:", error);
      showToast(
        getApiErrorMessage(error, "Failed to publish winners"),
        "error",
        4000,
      );
    } finally {
      setSubmitting(false);
      setConfirmingPublish(false);
      setConfirmingMissingRewards(false);
    }
  };

  const previewCategories = useMemo<PreviewCategory[]>(() => {
    const toPreviewWinner = (w: WinnerChipItem) => ({
      userId: w.userId,
      userName: w.userName,
      profilePictureUrl: w.profilePictureUrl,
    });

    const categories: PreviewCategory[] = [
      {
        title: "1st Prize",
        slot: 1,
        reward: contest?.reward1st,
        winners: winner1st.map(toPreviewWinner),
      },
    ];
    if (reward2ndConfigured) {
      categories.push({
        title: "2nd Prize",
        slot: 2,
        reward: contest?.reward2nd,
        winners: winner2nd.map(toPreviewWinner),
      });
    }
    if (reward3rdConfigured) {
      categories.push({
        title: "3rd Prize",
        slot: 3,
        reward: contest?.reward3rd,
        winners: winner3rd.map(toPreviewWinner),
      });
    }
    if (additionalWinnersConfigured) {
      categories.push({
        title: "Additional Winners",
        tokenReward: contest?.tokenRewardAdditionalWinners ?? undefined,
        winners: additionalWinners.map(toPreviewWinner),
      });
    }
    return categories;
  }, [contest, winner1st, winner2nd, winner3rd, additionalWinners, reward2ndConfigured, reward3rdConfigured, additionalWinnersConfigured]);

  const now = new Date();
  const minDisplayDate =
    contestEndDate && contestEndDate.getTime() > now.getTime() ? contestEndDate : now;
  // Fall back to the date the calendar will default to when nothing is
  // selected yet, so the past-time restriction is already active on the
  // very first click that lands on today — not just on subsequent opens
  // once state has caught up.
  const effectiveDisplayFrom = displayFrom ?? minDisplayDate;
  // For an already-published announcement, `displayFrom` can be a past start
  // date (it already started). The End Date can never precede "now" though,
  // so floor at minDisplayDate (which is itself already >= now) rather than
  // falling back to a stale, possibly-past `displayFrom`.
  const minDisplayToDate =
    displayFrom && displayFrom.getTime() > minDisplayDate.getTime()
      ? displayFrom
      : minDisplayDate;
  const effectiveDisplayTo = displayTo ?? displayFrom ?? minDisplayDate;
  const isDisplayFromToday =
    effectiveDisplayFrom.toDateString() === now.toDateString();
  const isDisplayToSameAsFrom =
    !!displayFrom && effectiveDisplayTo.toDateString() === displayFrom.toDateString();
  const isDisplayToToday = effectiveDisplayTo.toDateString() === now.toDateString();

  // Additional Winners can only be picked after every configured prize slot
  // above it (1st, and 2nd/3rd if their reward exists) has a winner.
  const requiredPrizeWinnersFilled =
    winner1st.length > 0 &&
    (!reward2ndConfigured || winner2nd.length > 0) &&
    (!reward3rdConfigured || winner3rd.length > 0);
  const additionalWinnersBlockedMessage = !winnersLocked && !requiredPrizeWinnersFilled
    ? `Add the ${reward3rdConfigured ? "3rd" : reward2ndConfigured ? "2nd" : "1st"} Prize winner before adding an Additional Winner.`
    : undefined;

  const hasAnyWinnerSelected =
    winner1st.length > 0 || winner2nd.length > 0 || winner3rd.length > 0 || additionalWinners.length > 0;
  const canPublish = hasAnyWinnerSelected && !!displayFrom && !!displayTo;

  /** One 1st/2nd/3rd Prize slot — single-select winner picker. */
  const renderPrizeSlot = (place: 1 | 2 | 3) => {
    const winnerState = place === 1 ? winner1st : place === 2 ? winner2nd : winner3rd;
    const setWinnerState = place === 1 ? setWinner1st : place === 2 ? setWinner2nd : setWinner3rd;
    const reward = place === 1 ? contest?.reward1st : place === 2 ? contest?.reward2nd : contest?.reward3rd;
    const configured = place === 1 ? true : place === 2 ? reward2ndConfigured : reward3rdConfigured;
    const priorFilled = place === 1 ? true : place === 2 ? winner1st.length > 0 : winner2nd.length > 0;
    const errorKey: keyof WinnerErrors = place === 1 ? "winner1st" : place === 2 ? "winner2nd" : "winner3rd";
    const label = `${ordinal(place)} Prize`;

    if (!configured) {
      return (
        <div key={place} className="rounded-xl border border-dashed border-gray-200 p-4 bg-gray-50/50">
          <h3 className="text-sm font-bold text-grease-100">{label} Winner</h3>
          <p className="text-xs text-gray-400 italic mt-1">
            {label} reward has not been added.
          </p>
        </div>
      );
    }

    const disabled = winnersLocked || !priorFilled || submitting;
    const blockedMessage =
      !winnersLocked && !priorFilled
        ? `Add the ${ordinal(place - 1)} Prize winner before adding a ${label} winner.`
        : undefined;

    const otherIds = [
      ...(place !== 1 ? winner1st.map((w) => w.userId) : []),
      ...(place !== 2 ? winner2nd.map((w) => w.userId) : []),
      ...(place !== 3 ? winner3rd.map((w) => w.userId) : []),
      ...additionalWinners.map((w) => w.userId),
    ];

    return (
      <div key={place} className="rounded-xl border border-gray-200 p-4 space-y-3 bg-gray-50/50">
        <div>
          <h3 className="text-sm font-bold text-grease-100">{label} Winner</h3>
          <p className="text-xs text-lightgray-200 mt-0.5">{reward}</p>
        </div>

        <div>
          <label className="block text-xs font-semibold text-grease-100 mb-1.5">
            Select Winner <span className="text-red-500">*</span>
          </label>
          <WinnerUserPicker
            selectedUsers={winnerState}
            singleSelect
            onAddUser={(user) => {
              setWinnerState([
                {
                  userId: user.userId,
                  userName: user.userName,
                  email: user.email,
                  profilePictureUrl: user.profilePictureUrl,
                },
              ]);
              clearSlotError(errorKey);
            }}
            onRemoveUser={() => setWinnerState([])}
            onRetryCredit={handleRetryCredit}
            retryingWinnerIds={retryingWinnerIds}
            excludeUserIds={otherIds}
            error={errors[errorKey]}
            disabled={disabled}
            placeholder={`Search and select ${label.toLowerCase()} winner...`}
          />
          {blockedMessage && (
            <p className="text-xs text-amber-600 mt-1.5">{blockedMessage}</p>
          )}
          {errors[errorKey] && (
            <p className="text-xs text-red-500 mt-1.5">{errors[errorKey]}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Page Header */}
      <section className="bg-white px-4 py-5 md:px-6 md:py-6 rounded-xl flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleNavigateAway}
            className="w-8 h-8 rounded-lg border border-[#D1D5DB] flex items-center justify-center text-grease-100 hover:bg-gray-50 transition-colors"
            title="Back to Giveaway Management"
            aria-label="Back to Giveaway Management"
          >
            <i className="st-arrow-left text-xs" aria-hidden="true" />
          </button>
          <h1 className="text-xl font-bold text-grease-100 tracking-tight">
            Giveaway Winner Management
          </h1>
        </div>
      </section>

      {loading ? (
        <div className="bg-white p-4 md:p-6 rounded-xl mt-6">
          <p className="text-center py-12 text-lightgray-200 text-sm">
            Loading...
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 mt-6 items-start">
          {/* Winner Form */}
          <form
            onSubmit={handlePublishClick}
            noValidate
            className="bg-white rounded-xl p-4 md:p-6 space-y-2.5 flex flex-col"
          >
            {/* Contest summary row */}
            <div className="pb-4 border-b border-gray-100">
              <span className="text-xs font-medium text-lightgray-200 uppercase tracking-wider">
                Contest
              </span>
              <p className="font-bold text-base text-grease-100 mt-0.5">
                {contest?.contestName || "-"}
              </p>
            </div>

            {winnersLocked && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-start gap-2 text-xs text-royalblue-100">
                <Info className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
                <span>
                  Winners have already been published for this contest. Winner selection is locked; only the Display Period can still be changed, and only while it is still in the future.
                </span>
              </div>
            )}

            {renderPrizeSlot(1)}
            {renderPrizeSlot(2)}
            {renderPrizeSlot(3)}

            {additionalWinnersConfigured && (
              <div className="rounded-xl border border-gray-200 p-4 space-y-3 bg-gray-50/50">
                <div>
                  <h3 className="text-sm font-bold text-grease-100">Additional Winners</h3>
                  <p className="text-xs text-lightgray-200 mt-0.5">
                    Up to {additionalWinnersCap} winner{additionalWinnersCap === 1 ? "" : "s"}
                    {contest?.tokenRewardAdditionalWinners
                      ? ` • ${contest.tokenRewardAdditionalWinners} Tokens each`
                      : ""}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-grease-100 mb-1.5">
                    Select Winner(s)
                  </label>
                  <WinnerUserPicker
                    selectedUsers={additionalWinners}
                    onAddUser={handleAddAdditionalWinner}
                    onRemoveUser={handleRemoveAdditionalWinner}
                    onRetryCredit={handleRetryCredit}
                    retryingWinnerIds={retryingWinnerIds}
                    excludeUserIds={[...winner1st, ...winner2nd, ...winner3rd].map((w) => w.userId)}
                    error={errors.additionalWinners}
                    disabled={winnersLocked || !requiredPrizeWinnersFilled || submitting}
                    capReached={additionalWinners.length >= additionalWinnersCap}
                    onDismissWarning={() => clearSlotError("additionalWinners")}
                    placeholder="Search and add additional winners..."
                  />
                  {additionalWinnersBlockedMessage && (
                    <p className="text-xs text-amber-600 mt-1.5">{additionalWinnersBlockedMessage}</p>
                  )}
                  {errors.additionalWinners && (
                    <p className="text-xs text-red-500 mt-1.5">{errors.additionalWinners}</p>
                  )}
                </div>
              </div>
            )}

            {/* Shared Display Period */}
            <div>
              <FormField
                label="Display Period"
                required
                error={errors.displayPeriod}
              >
                <div className="flex flex-col md:flex-row gap-3">
                  <div
                    className={`relative flex-1 rounded-lg border border-[#D1D5DB] px-3 py-2 bg-white ${
                      displayStartLocked ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer"
                    }`}
                    onClick={() => !displayStartLocked && displayFromPickerRef.current?.setOpen(true)}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-grease-100">
                        Start Date &amp; Time
                      </p>
                      {!displayStartLocked && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            displayFromPickerRef.current?.setOpen(true);
                          }}
                          className="st-calendar text-lightgray-200 text-sm hover:text-grease-100 cursor-pointer"
                          aria-label="Open display start date picker"
                        />
                      )}
                    </div>
                    <DatePicker
                      {...datePickerPopperProps}
                      popperClassName={`${datePickerPopperProps.popperClassName} st-giveaway-datepicker`}
                      ref={displayFromPickerRef}
                      id="displayFrom"
                      selected={displayFrom}
                      disabled={displayStartLocked || submitting}
                      onChange={(date) => {
                        if (date) {
                          let finalDate = date;
                          const rightNow = new Date();
                          const isToday = date.toDateString() === rightNow.toDateString();
                          if (!displayFrom && !isToday) {
                            finalDate = new Date(date);
                            finalDate.setHours(0, 0, 0, 0);
                          }

                          // A bare day-click skips the time list's
                          // disabled-time check, so it can land at/before
                          // now — clamp forward to the next valid 15-min slot.
                          if (isToday && finalDate.getTime() <= rightNow.getTime()) {
                            const nextSlotMinutes =
                              Math.floor(rightNow.getMinutes() / 15) * 15 + 15;
                            finalDate = new Date(date);
                            finalDate.setHours(rightNow.getHours(), nextSlotMinutes, 0, 0);
                          }

                          setDisplayFrom(finalDate);
                          if (
                            displayTo &&
                            displayTo.getTime() - finalDate.getTime() < MIN_DISPLAY_GAP_MS
                          ) {
                            setDisplayTo(null);
                          }
                        } else {
                          setDisplayFrom(null);
                        }
                        clearSlotError("displayPeriod");
                      }}
                      showTimeSelect
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      timeFormat="HH:mm"
                      minDate={minDisplayDate}
                      openToDate={displayFrom ?? minDisplayDate}
                      minTime={isDisplayFromToday ? now : undefined}
                      maxTime={
                        isDisplayFromToday
                          ? new Date(new Date().setHours(23, 59, 59, 999))
                          : undefined
                      }
                      placeholderText="dd/mm/yyyy"
                      className={`w-full p-0 border-0 text-sm focus:ring-0 focus:outline-none ${
                        displayStartLocked
                          ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                          : "bg-white text-grease-200 cursor-pointer"
                      }`}
                      wrapperClassName="w-full"
                      onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
                    />
                  </div>

                  <div
                    className={`relative flex-1 rounded-lg border border-[#D1D5DB] px-3 py-2 bg-white ${
                      displayPeriodLocked ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer"
                    }`}
                    onClick={() => !displayPeriodLocked && displayToPickerRef.current?.setOpen(true)}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-grease-100">
                        End Date &amp; Time
                      </p>
                      {!displayPeriodLocked && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            displayToPickerRef.current?.setOpen(true);
                          }}
                          className="st-calendar text-lightgray-200 text-sm hover:text-grease-100 cursor-pointer"
                          aria-label="Open display end date picker"
                        />
                      )}
                    </div>
                    <DatePicker
                      {...datePickerPopperProps}
                      popperClassName={`${datePickerPopperProps.popperClassName} st-giveaway-datepicker`}
                      ref={displayToPickerRef}
                      id="displayTo"
                      selected={displayTo}
                      disabled={displayPeriodLocked || submitting}
                      onChange={(date) => {
                        if (date) {
                          let finalDate = date;
                          const rightNow = new Date();
                          const isToday = date.toDateString() === rightNow.toDateString();
                          const isSameAsFrom =
                            !!displayFrom &&
                            date.toDateString() === displayFrom.toDateString();

                          if (!displayTo && !isToday && !isSameAsFrom) {
                            finalDate = new Date(date);
                            finalDate.setHours(0, 0, 0, 0);
                          }

                          // A bare day-click (or a stale time carried over
                          // from a previous selection) skips the time list's
                          // disabled-time check, so it can land at/before now
                          // or before the minimum gap from the start — clamp
                          // forward to the next valid 15-min slot.
                          const bounds: number[] = [];
                          if (isToday) bounds.push(rightNow.getTime());
                          if (isSameAsFrom && displayFrom) {
                            bounds.push(displayFrom.getTime() + MIN_DISPLAY_GAP_MS);
                          }
                          if (bounds.length) {
                            const minAllowed = new Date(Math.max(...bounds));
                            if (finalDate.getTime() < minAllowed.getTime()) {
                              const nextSlotMinutes =
                                Math.floor(minAllowed.getMinutes() / 15) * 15 + 15;
                              finalDate = new Date(date);
                              finalDate.setHours(minAllowed.getHours(), nextSlotMinutes, 0, 0);
                            }
                          }

                          setDisplayTo(finalDate);
                        } else {
                          setDisplayTo(null);
                        }
                        clearSlotError("displayPeriod");
                      }}
                      showTimeSelect
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      timeFormat="HH:mm"
                      minDate={minDisplayToDate}
                      openToDate={displayTo ?? displayFrom ?? minDisplayDate}
                      minTime={
                        isDisplayToSameAsFrom && displayFrom
                          ? new Date(displayFrom.getTime() + MIN_DISPLAY_GAP_MS)
                          : isDisplayToToday
                          ? now
                          : undefined
                      }
                      maxTime={
                        isDisplayToSameAsFrom || isDisplayToToday
                          ? new Date(new Date().setHours(23, 59, 59, 999))
                          : undefined
                      }
                      placeholderText="dd/mm/yyyy"
                      className={`w-full p-0 border-0 text-sm focus:ring-0 focus:outline-none ${
                        displayPeriodLocked
                          ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                          : "bg-white text-grease-200 cursor-pointer"
                      }`}
                      wrapperClassName="w-full"
                      onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
                    />
                  </div>
                </div>
              </FormField>
              <p className="text-[11px] text-lightgray-200 mt-1.5">
                Winner post will be visible to users only within the selected date range.
              </p>
            </div>

            <p className="flex items-start gap-1.5 text-xs text-lightgray-200">
              <Info className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
              Winners will see the announcement on their home screen within the selected display period. Tokens are credited to Additional Winners upon publishing.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col-reverse md:flex-row md:justify-end gap-3 pt-4 border-t border-gray-100">
              <ActionButton
                variant="neutral"
                onClick={handleNavigateAway}
                disabled={submitting}
              >
                Cancel
              </ActionButton>
              <ActionButton
                type="submit"
                variant="amber"
                loading={submitting}
                disabled={submitting || !canPublish}
              >
                {winnersLocked ? "Update Announcement" : "Publish Announcement"}
              </ActionButton>
            </div>
          </form>

          {/* Live Preview Panel */}
          <div className="bg-white rounded-xl p-4 md:p-6 space-y-4">
            <div>
              <span className="font-bold text-grease-100">Live Preview</span>{" "}
              <span className="text-xs text-lightgray-200">
                (Real-time announcement card as seen by users)
              </span>
            </div>

            <WinnerPreviewCard categories={previewCategories} />

            <div className="flex items-start gap-2 rounded-lg bg-lightblue-100 p-3 text-xs text-grease-200">
              <Info
                className="w-4 h-4 shrink-0 text-violet-100"
                aria-hidden="true"
              />
              <span>
                The announcement banner will dynamically display each winning user's name and prize tier.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal: rewards configured but no winner added */}
      <ConfirmDeleteModal
        isOpen={confirmingMissingRewards}
        title="Publish Without All Winners?"
        message={
          <p className="text-sm text-gray-700 leading-relaxed">
            Rewards for the <strong>{joinWithAnd(missingRewardCategories)}</strong> have been defined in the Contest Details, but no winners have been added. Are you sure you want to save the contest without adding these winners?
          </p>
        }
        confirmLabel="Continue"
        cancelLabel="Go Back"
        isDestructive={false}
        onConfirm={() => {
          setConfirmingMissingRewards(false);
          setConfirmingPublish(true);
        }}
        onCancel={() => setConfirmingMissingRewards(false)}
        configurationName=""
      />

      {/* Confirmation Modal: Publish/Update Announcement */}
      <ConfirmDeleteModal
        isOpen={confirmingPublish}
        title={winnersLocked ? "Update Winner Announcement" : "Publish Winner Announcement"}
        message={
          <p className="text-sm text-gray-700 leading-relaxed">
            {winnersLocked
              ? "Updating the Display Period will change when this announcement is visible to users. Continue?"
              : "Publishing will credit tokens to the winners and make the announcement visible to users. Continue?"}
          </p>
        }
        confirmLabel={winnersLocked ? "Update" : "Publish"}
        cancelLabel="Cancel"
        isDestructive={false}
        onConfirm={executePublish}
        onCancel={() => setConfirmingPublish(false)}
        confirmDisabled={submitting}
        configurationName=""
      />

      {/* Confirmation Modal: Discard Unsaved Changes */}
      <ConfirmDeleteModal
        isOpen={confirmingDiscard}
        title="Discard unsaved changes?"
        message={
          <p className="text-sm text-gray-700 leading-relaxed">
            You have unsaved changes to winner configuration. Are you sure you want to leave this page?
          </p>
        }
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        isDestructive={true}
        onConfirm={() => {
          setConfirmingDiscard(false);
          navigate(LIST_PATH);
        }}
        onCancel={() => setConfirmingDiscard(false)}
        configurationName=""
      />
    </>
  );
};

export default GiveawayWinnerForm;
